import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TextField, Button, Alert } from "@mui/material";
import { usePergolaConfigurationBuilderActions } from "../../../../../../store/reducers/pergola_configuration_builder";
import { useStoreDispatch, useStoreSelector } from "../../../../../../hooks/StoreHooks";

type Props = {
  lato: "Retro" | "Fronte" | "Destra" | "Sinistra";
};

type DirezioneTubolare =
  | "Retro" | "Fronte" | "Destra" | "Sinistra"
  | "Retro2" | "Fronte2" | "Destra2" | "Sinistra2";

export const TubolariChoice = ({ lato }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const { pergolaConfigurationBuilder } = useStoreSelector((store) => store);
  const { setStepActive, setStepComplete, setTubolareSingolo } = usePergolaConfigurationBuilderActions();

  const fioriereAttuali = pergolaConfigurationBuilder.fioriere;
  const dimensioni = pergolaConfigurationBuilder.configuration?.dimensioni;

  const isLatoLaterale = lato === "Sinistra" || lato === "Destra";
  const misuraRilevante = isLatoLaterale ? dimensioni?.profondita : dimensioni?.larghezza;
  const misura = misuraRilevante ?? 2000;
  const misuraNome = isLatoLaterale ? "profondità" : "larghezza";

  const CONFLITTI_TUBOLARI = {
    Fronte: { principale: "pilastro_H4", secondario: "pilastro_H3" },
    Retro:  { principale: "pilastro_H1", secondario: "pilastro_H2" },
    Sinistra: { principale: "pilastro_H4", secondario: "pilastro_H1" },
    Destra: { principale: "pilastro_H3", secondario: "pilastro_H2" },
  } as const;

  const conflitti = CONFLITTI_TUBOLARI[lato];

  // Conflitto con fioriere
  const haFiorieraPrincipale = fioriereAttuali.some(f => f.direzione === conflitti.principale);
  const haFiorieraSecondaria = fioriereAttuali.some(f => f.direzione === conflitti.secondario);

  // Calcolo massimo tubolari possibili: (misura - 270mm margine) / (40mm tubolare + 40mm spazio)
  const maxTubolari = Math.floor((misura - 270) / 80);

  const [numeroMain, setNumeroMain] = useState<number>(0);
  const [numeroSecond, setNumeroSecond] = useState<number>(0);
  const [touched, setTouched] = useState<{ main: boolean; second: boolean }>({
    main: false,
    second: false,
  });

  const totaleTubolari = numeroMain + numeroSecond;
  const isValid = totaleTubolari <= maxTubolari && totaleTubolari >= 0;
  const showError = (touched.main || touched.second) && !isValid;

  // Caricamento valori esistenti dalla configurazione
  useEffect(() => {
    const tubolareMain = pergolaConfigurationBuilder.tubolari.find((t) => t.direzione === lato);
    const tubolareSecond = pergolaConfigurationBuilder.tubolari.find(
      (t) => t.direzione === `${lato}2` as DirezioneTubolare
    );

    const main = tubolareMain?.numero ?? 0;
    const second = tubolareSecond?.numero ?? 0;

    setNumeroMain(main);
    setNumeroSecond(second);
    setTouched({ main: main > 0, second: second > 0 });
  }, [lato, pergolaConfigurationBuilder.tubolari]);

  // Validazione iniziale della configurazione
  useEffect(() => {
    if (!id || isNaN(Number(id)) || !pergolaConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      dispatch(setStepActive({ stepSlug: "estetica", isActive: true }));
    }
  }, [id, navigate, pergolaConfigurationBuilder.configuration, dispatch]);

  const handleChange = (direzione: DirezioneTubolare, value: string) => {
    const num = Math.max(0, Math.floor(parseInt(value, 10) || 0));
    // Limita automaticamente al massimo consentito
    const limitedNum = Math.min(num, maxTubolari);

    if (direzione === lato) {
      setNumeroMain(limitedNum);
      setTouched((prev) => ({ ...prev, main: true }));
    } else if (direzione === `${lato}2` as DirezioneTubolare) {
      setNumeroSecond(limitedNum);
      setTouched((prev) => ({ ...prev, second: true }));
    }

    dispatch(
      setTubolareSingolo({
        direzione,
        numero: limitedNum,
      })
    );
  };

  const submit = () => {
    if (!isValid) return;

    dispatch(setStepComplete({ stepSlug: "illuminazioni", isComplete: true }));
    navigate(`/pergola-configurations/${id}/next-step`);
  };

  return (
    <div className="row h-full m-0">
      <div className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          noValidate
        >
          {/* Tubolari principali */}
          {!haFiorieraPrincipale && (
            <TextField
              label={`Numero tubolari - ${lato}`}
              type="number"
              value={numeroMain}
              onChange={(e) => handleChange(lato, e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, main: true }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 1 }}
              helperText={
                touched.main && numeroMain === 0
                  ? "Lascia 0 se non desideri tubolari su questo lato"
                  : undefined
              }
              error={showError}
            />
          )}

          {/* Tubolari secondari */}
          {!haFiorieraSecondaria && (
            <TextField
              label={`Numero tubolari - ${lato} (secondari)`}
              type="number"
              value={numeroSecond}
              onChange={(e) =>
                handleChange(`${lato}2` as DirezioneTubolare, e.target.value)
              }
              onBlur={() => setTouched((prev) => ({ ...prev, second: true }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 1 }}
              error={showError}
            />
          )}

          {/* Box informativo + errori */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium">
              Totale tubolari selezionati: <strong>{totaleTubolari}</strong> / {maxTubolari}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Calcolato su {misuraNome} di {misura} mm
            </p>

            {showError && (
              <Alert severity="error" className="mt-3">
                Il totale non può superare {maxTubolari} tubolari per questo lato. Riduci il numero inserito.
              </Alert>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};